import { Type, Schema } from "@google/genai";
import type { FmeaStandard } from "../types";

// ============================================================================
// AIAG-VDA & AIAG V4 & SAE J1739 (RPN-based)
// ============================================================================
export const aiagVdaFmeaSchema: Schema = {
    type: Type.OBJECT,
    properties: {
        function: { type: Type.STRING, description: "Top-level system function" },
        itemFunction: { type: Type.STRING, description: "Specific item/component function" },
        requirement: { type: Type.STRING, description: "Design requirement being violated" },
        failureMode: { type: Type.STRING, description: "How the item fails to meet requirement" },
        effects: { type: Type.STRING, description: "Consequences of failure" },
        S: { type: Type.NUMBER, description: "Severity (1-10)" },
        O: { type: Type.NUMBER, description: "Occurrence (1-10)" },
        D: { type: Type.NUMBER, description: "Detection (1-10)" },
        preventionControls: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Prevention controls" },
        detectionControls: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Detection controls" },
        notes: { type: Type.STRING, description: "Rationale and recommendations" }
    },
    required: ["function", "itemFunction", "requirement", "failureMode", "effects", "S", "O", "D", "notes"]
};

// ============================================================================
// ISO 14971:2020 (Medical Devices)
// ============================================================================
export const iso14971FmeaSchema: Schema = {
    type: Type.OBJECT,
    properties: {
        function: { type: Type.STRING, description: "Device function" },
        hazardId: { type: Type.STRING, description: "Unique hazard identifier (e.g., HAZ-001)" },
        hazardousSituation: { type: Type.STRING, description: "Situation that could lead to harm" },
        foreseableMisuse: { type: Type.STRING, description: "Reasonably foreseeable misuse scenario" },
        harm: { type: Type.STRING, description: "Physical injury or damage to health" },
        P1: { type: Type.NUMBER, description: "Probability of hazardous situation (1-5)" },
        P2: { type: Type.NUMBER, description: "Probability of harm given situation (1-5)" },
        S: { type: Type.NUMBER, description: "Severity of harm (1-5)" },
        riskEstimate: { type: Type.NUMBER, description: "P1 × P2 × S" },
        riskAcceptability: { type: Type.STRING, description: "Acceptable | ALARP | Unacceptable" },
        riskControlMeasures: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Inherent safety, protective measures, information" },
        residualRisk: { type: Type.STRING, description: "Risk remaining after controls" },
        notes: { type: Type.STRING, description: "Rationale, benefit-risk, traceability" }
    },
    required: ["function", "hazardId", "hazardousSituation", "harm", "P1", "P2", "S", "riskEstimate", "riskAcceptability", "notes"]
};

// ============================================================================
// MIL-STD-1629A (Military/Aerospace FMECA)
// ============================================================================
export const milStdFmeaSchema: Schema = {
    type: Type.OBJECT,
    properties: {
        function: { type: Type.STRING, description: "System/subsystem function" },
        itemNumber: { type: Type.STRING, description: "Part/assembly number" },
        functionDescription: { type: Type.STRING, description: "Detailed function description" },
        failureMode: { type: Type.STRING, description: "Manner of failure" },
        failureEffect: { type: Type.STRING, description: "Local, next level, end effect" },
        missionPhase: { type: Type.STRING, description: "Launch | Orbit | Re-entry | Landing" },
        severityCategory: { type: Type.STRING, description: "I (Catastrophic) | II (Critical) | III (Marginal) | IV (Negligible)" },
        alpha: { type: Type.NUMBER, description: "Mode ratio (0-1): fraction of failures that are this mode" },
        beta: { type: Type.NUMBER, description: "Conditional probability (0-1): prob this mode causes critical effect" },
        lambda: { type: Type.NUMBER, description: "Failure rate (failures/million hours)" },
        criticalityNumber: { type: Type.NUMBER, description: "Cm = λ × α × β × mission_time" },
        correctiveAction: { type: Type.STRING, description: "Design change or mitigation" },
        notes: { type: Type.STRING, description: "Rationale, MIL-HDBK-217 reference" }
    },
    required: ["function", "failureMode", "failureEffect", "severityCategory", "alpha", "beta", "lambda", "notes"]
};

// ============================================================================
// ISO 26262:2018 (Automotive Functional Safety)
// ============================================================================
export const iso26262FmeaSchema: Schema = {
    type: Type.OBJECT,
    properties: {
        function: { type: Type.STRING, description: "Vehicle function (e.g., Electronic Steering)" },
        hazardousEvent: { type: Type.STRING, description: "Combination of hazard and operational situation" },
        operationalSituation: { type: Type.STRING, description: "Highway | Urban | Parking | etc." },
        S: { type: Type.NUMBER, description: "Severity: 0 (no injury) | 1 (light) | 2 (severe) | 3 (life-threatening)" },
        E: { type: Type.NUMBER, description: "Exposure: 0 (incredible) | 1 (low) | 2 (medium) | 3 (high) | 4 (very high)" },
        C: { type: Type.NUMBER, description: "Controllability: 0 (controllable) | 1 (simple) | 2 (normally) | 3 (uncontrollable)" },
        ASIL: { type: Type.STRING, description: "QM | A | B | C | D" },
        safetyGoal: { type: Type.STRING, description: "Top-level requirement to mitigate hazard" },
        safetyRequirements: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Derived functional safety requirements" },
        diagnosticCoverage: { type: Type.NUMBER, description: "DC percentage (60-99)" },
        hardwareFaultTolerance: { type: Type.NUMBER, description: "HFT: 0 | 1 | 2" },
        notes: { type: Type.STRING, description: "Rationale, ASIL decomposition, architecture" }
    },
    required: ["function", "hazardousEvent", "operationalSituation", "S", "E", "C", "ASIL", "safetyGoal", "notes"]
};

// ============================================================================
// IEC 61508 (Industrial Functional Safety)
// ============================================================================
export const iec61508FmeaSchema: Schema = {
    type: Type.OBJECT,
    properties: {
        function: { type: Type.STRING, description: "Safety-related function (e.g., Emergency Shutdown)" },
        hazardousEvent: { type: Type.STRING, description: "Event that could lead to harm" },
        C: { type: Type.NUMBER, description: "Consequence severity: 1 (minor) | 2 (serious) | 3 (death) | 4 (multiple deaths)" },
        F: { type: Type.NUMBER, description: "Frequency of exposure: 1 (rare) | 2 (frequent) | 3 (permanent)" },
        P: { type: Type.NUMBER, description: "Possibility of avoiding: 1 (possible) | 2 (almost impossible)" },
        SIL: { type: Type.STRING, description: "SIL 1 | SIL 2 | SIL 3 | SIL 4" },
        PFD: { type: Type.STRING, description: "Probability of Failure on Demand (e.g., 10⁻³)" },
        SFF: { type: Type.NUMBER, description: "Safe Failure Fraction (60-99%)" },
        HFT: { type: Type.NUMBER, description: "Hardware Fault Tolerance: 0 | 1 | 2" },
        safetyFunctions: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Safety instrumented functions" },
        notes: { type: Type.STRING, description: "Rationale, architecture (1oo1, 1oo2, 2oo3)" }
    },
    required: ["function", "hazardousEvent", "C", "F", "P", "SIL", "PFD", "SFF", "HFT", "notes"]
};

// ============================================================================
// Schema Mapping by Standard
// ============================================================================
export const FMEA_SCHEMAS: Record<FmeaStandard, Schema> = {
    'AIAG_V4': aiagVdaFmeaSchema,
    'AIAG_VDA': aiagVdaFmeaSchema,
    'SAE_J1739': aiagVdaFmeaSchema,
    'ISO_14971': iso14971FmeaSchema,
    'ISO_26262': iso26262FmeaSchema,
    'IEC_61508': iec61508FmeaSchema,
    'MIL_STD_1629A': milStdFmeaSchema,
    'CUSTOMER_CUSTOM': aiagVdaFmeaSchema // Default fallback
};

// ============================================================================
// Generation Response Schema (wraps items + report)
// ============================================================================
export const getFmeaGenerationResponseSchema = (itemSchema: Schema): Schema => ({
    type: Type.OBJECT,
    properties: {
        markdownReport: {
            type: Type.STRING,
            description: "Comprehensive markdown report explaining findings and recommendations"
        },
        newFmeaItems: {
            type: Type.ARRAY,
            items: itemSchema
        }
    },
    required: ["markdownReport", "newFmeaItems"]
});
